import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface LocationOptions {
  cidades: string[];
  bairros: string[];
  getBairrosByCidade: (cidade: string) => string[];
}

/**
 * Hook para buscar opções de localização (cidades e bairros) dos imóveis ativos
 */
export function useLocationOptions(imobiliariaId?: string): LocationOptions & { isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['location-options', imobiliariaId],
    queryFn: async () => {
      // Usa view pública do HouseBit (já filtra imóveis ativos)
      let query = supabase
        .from('imoveis_public')
        .select('endereco_cidade, endereco_bairro');

      if (imobiliariaId) {
        query = query.eq('imobiliaria_id', imobiliariaId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Extrair cidades e bairros únicos
      const cidadesSet = new Set<string>();
      const bairrosSet = new Set<string>();
      const bairrosPorCidade = new Map<string, Set<string>>();

      // Validar se é um nome válido (não apenas números/códigos IBGE)
      const isValidName = (value: string | undefined): value is string => {
        return !!value && value.length > 1 && !/^\d+$/.test(value);
      };

      (data as Array<{ endereco_cidade: string | null; endereco_bairro: string | null }>)?.forEach((imovel) => {
        const cidade = imovel.endereco_cidade?.trim();
        const bairro = imovel.endereco_bairro?.trim();

        if (isValidName(cidade)) {
          cidadesSet.add(cidade);
          
          if (isValidName(bairro)) {
            if (!bairrosPorCidade.has(cidade)) {
              bairrosPorCidade.set(cidade, new Set());
            }
            bairrosPorCidade.get(cidade)!.add(bairro);
          }
        }

        if (isValidName(bairro)) {
          bairrosSet.add(bairro);
        }
      });

      return {
        cidades: Array.from(cidadesSet).sort((a, b) => a.localeCompare(b, 'pt-BR')),
        bairros: Array.from(bairrosSet).sort((a, b) => a.localeCompare(b, 'pt-BR')),
        bairrosPorCidade: Object.fromEntries(
          Array.from(bairrosPorCidade.entries()).map(([cidade, bairros]) => [
            cidade,
            Array.from(bairros).sort((a, b) => a.localeCompare(b, 'pt-BR'))
          ])
        ),
      };
    },
    enabled: !!imobiliariaId,
    staleTime: 1000 * 60 * 10, // Cache por 10 minutos
  });

  const getBairrosByCidade = (cidade: string): string[] => {
    if (!cidade || !data?.bairrosPorCidade) {
      return data?.bairros || [];
    }
    return data.bairrosPorCidade[cidade] || [];
  };

  return {
    cidades: data?.cidades || [],
    bairros: data?.bairros || [],
    getBairrosByCidade,
    isLoading,
  };
}
